import React from 'react';

import { Divider, IconButton, Typography } from '@mui/material';
import { Spacer } from 'src/components';
import { AddIcon } from 'src/components/icons/AddIcon';
import { GreenTickIcon } from 'src/components/icons/GreenTickIcon';
import { SmallSigningIcon } from 'src/components/icons/SmallSigningIcon';
import { UpcomingIcon } from 'src/components/icons/UpcomingIcon';
import { Apollo } from 'src/gql/generated';
import { getIsWorkflowStepComplete } from 'src/workflow/helpers';
import type { WorkflowStep } from 'src/workflow/types';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  headerLeft: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1.25),
  },
  headerRoot: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'nowrap',
    padding: theme.spacing(0, 2),
  },
}));

interface ActionGroupHeaderProps {
  isActiveGroup: boolean;
  setNewActionRow: (open: boolean) => void;
  step: WorkflowStep;
  stepNumber: number;
  workflowId: UUID;
  workflowState: Apollo.WorkflowState;
  cardAction?: React.ReactNode;
}

export function SigningGroupHeader({
  cardAction,
  isActiveGroup,
  step,
  stepNumber,
  workflowState,
}: ActionGroupHeaderProps): JSX.Element {
  const { classes } = useStyles();

  return (
    <>
      <div className={classes.headerRoot}>
        <div className={classes.headerLeft}>
          <Typography variant="subtitle2">Group {stepNumber}</Typography>
          <SigningGroupAlert
            isActiveGroup={isActiveGroup}
            step={step}
            workflowState={workflowState}
          />
        </div>
        <Spacer />
        {cardAction}
      </div>
    </>
  );
}

const useSigningAlertStyles = makeStyles()(theme => ({
  alert: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
  },
}));

export function SigningGroupAlert({
  isActiveGroup,
  step,
  workflowState,
}: {
  isActiveGroup: boolean;
  step: WorkflowStep;
  workflowState: Apollo.WorkflowState;
}): JSX.Element | null {
  switch (workflowState) {
    case Apollo.WorkflowState.Signing:
      return <SigningAlert isActiveGroup={isActiveGroup} step={step} />;
    case Apollo.WorkflowState.Signed:
    case Apollo.WorkflowState.Complete:
      return <SigningCompleteAlert />;
    default:
      return null;
  }
}

function SigningAlert({
  isActiveGroup,
  step,
}: {
  isActiveGroup: boolean;
  step: WorkflowStep;
}): JSX.Element | null {
  const { classes } = useSigningAlertStyles();
  const isStepCompleted = getIsWorkflowStepComplete(step);

  if (isStepCompleted) return <SigningCompleteAlert />;

  if (isActiveGroup) {
    return (
      <div className={classes.alert}>
        <Divider flexItem orientation="vertical" />
        <SmallSigningIcon />
        <Typography>Actively signing</Typography>
      </div>
    );
  }

  return (
    <div className={classes.alert}>
      <Divider flexItem orientation="vertical" />
      <UpcomingIcon />
      <Typography>Upcoming</Typography>
    </div>
  );
}

function SigningCompleteAlert(): JSX.Element {
  const { classes } = useSigningAlertStyles();

  return (
    <div className={classes.alert}>
      <Divider flexItem orientation="vertical" />
      <GreenTickIcon />
      <Typography>Signing complete</Typography>
    </div>
  );
}

export function NewStepHeader({ stepNumber }: { stepNumber: number }): JSX.Element {
  const { classes } = useStyles();

  return (
    <div className={classes.headerRoot}>
      <div className={classes.headerLeft}>
        <Typography variant="subtitle2">Group {stepNumber}</Typography>
      </div>
      <Spacer />
      <IconButton disabled size="small">
        <AddIcon />
      </IconButton>
    </div>
  );
}
